import { A } from "@solidjs/router";
import { For, Show } from "solid-js";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader } from "../../lib/async.ts";
import { formatMinimum } from "../../lib/format.ts";
import { relevancyStatus } from "../../api/domain.ts";
import { ok } from "../../api/result.ts";
import type {
  Capability,
  Persona,
  Relevancy,
  RelevancyQuestionnaire,
  Requirement,
  Topic,
  View,
} from "../../api/domain.ts";
import styles from "../admin/admin.module.scss";
import { compareHref } from "./portal.ts";

type World = {
  personas: Persona[];
  relevancies: Relevancy[];
  requirements: Requirement[];
  questionnaires: RelevancyQuestionnaire[];
  capabilities: Capability[];
  topics: Topic[];
  views: View[];
};

/**
 * The customer's own decisions, read back: every persona with the weights it
 * gave the capabilities and the must-haves it declared. Nothing here is
 * editable; the decisions are made through the questionnaires and stay put.
 */
export const CustomerPersonaPage = () => {
  const api = useApi();
  const i18n = useI18n();

  const world = createLoader<World>(async () => {
    const [personas, relevancies, requirements, questionnaires, capabilities, topics, views] =
      await Promise.all([
        api.list("personas", { lang: i18n.locale() }),
        api.list("relevancies"),
        api.list("requirements"),
        api.list("relevancy-questionnaires"),
        api.list("capabilities", { lang: i18n.locale() }),
        api.list("topics", { lang: i18n.locale() }),
        api.list("views", { lang: i18n.locale() }),
      ]);
    for (const result of [
      personas,
      relevancies,
      requirements,
      questionnaires,
      capabilities,
      topics,
      views,
    ]) {
      if (!result.ok) return result;
    }
    return ok({
      personas: personas.ok ? personas.value : [],
      relevancies: relevancies.ok ? relevancies.value : [],
      requirements: requirements.ok ? requirements.value : [],
      questionnaires: questionnaires.ok ? questionnaires.value : [],
      capabilities: capabilities.ok ? capabilities.value : [],
      topics: topics.ok ? topics.value : [],
      views: views.ok ? views.value : [],
    });
  });

  const capabilityOf = (value: World, id: string): Capability | undefined =>
    value.capabilities.find((capability) => capability.id === id);
  const capabilityLabel = (value: World, id: string): string =>
    capabilityOf(value, id)?.label ?? id;
  const topicTitle = (value: World, capability: string): string => {
    const topic = capabilityOf(value, capability)?.topic;
    return value.topics.find((entry) => entry.id === topic)?.title ?? "";
  };

  /** The persona's active ratings, in topic order so the table reads like the catalog. */
  const ratingsOf = (value: World, persona: Persona): Relevancy[] =>
    value.relevancies
      .filter((entry) => entry.persona === persona.id && entry.active)
      .sort((left, right) => {
        const byTopic = topicTitle(value, left.capability).localeCompare(
          topicTitle(value, right.capability),
          i18n.locale(),
        );
        return byTopic !== 0
          ? byTopic
          : capabilityLabel(value, left.capability).localeCompare(
              capabilityLabel(value, right.capability),
              i18n.locale(),
            );
      });

  const requirementsOf = (value: World, persona: Persona): Requirement[] =>
    value.requirements.filter((entry) => entry.persona === persona.id && entry.active);

  /** The relevancy questionnaire still waiting for this persona, if one is. */
  const openQuestionnaire = (value: World, persona: Persona): RelevancyQuestionnaire | undefined =>
    value.questionnaires.find(
      (entry) =>
        entry.persona === persona.id && entry.active && relevancyStatus(entry) !== "evaluated",
    );

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().portal.personaTitle}</h1>
      </div>

      <Async value={world.value()} onRetry={world.reload}>
        {(value) => (
          <Show
            when={value.personas.length > 0}
            fallback={
              <div class={styles.stack}>
                <p class={styles.note}>{i18n.t().portal.noPersona}</p>
                <p class={styles.note}>{i18n.t().portal.noWeighting}</p>
              </div>
            }
          >
            <For each={value.personas}>
              {(persona) => (
                <Panel
                  title={persona.name}
                  description={persona.description ?? undefined}
                  actions={
                    <A href={compareHref(value.views, persona)}>{i18n.t().portal.compareFor}</A>
                  }
                >
                  <section class={styles.stack}>
                    <h3>{i18n.t().portal.ratings}</h3>
                    <Show
                      when={openQuestionnaire(value, persona)}
                      fallback={
                        <Table
                          rows={ratingsOf(value, persona)}
                          rowKey={(rating) => rating.id}
                          emptyLabel={i18n.t().portal.noRatings}
                          columns={[
                            {
                              key: "topic",
                              header: i18n.t().common.topic,
                              cell: (rating) => topicTitle(value, rating.capability),
                            },
                            {
                              key: "capability",
                              header: i18n.t().common.capability,
                              cell: (rating) => capabilityLabel(value, rating.capability),
                            },
                            {
                              key: "rating",
                              header: i18n.t().relevancy.rating,
                              cell: (rating) => i18n.t().relevancy.scale[rating.rating],
                            },
                            {
                              key: "note",
                              header: i18n.t().relevancy.note,
                              cell: (rating) => rating.note ?? "",
                            },
                          ]}
                        />
                      }
                    >
                      {(questionnaire) => (
                        <p class={styles.open}>
                          <span>{i18n.t().portal.ratingsOpen}</span>
                          <A href={`/questionnaires?rate=${encodeURIComponent(questionnaire().id)}`}>
                            {i18n.t().portal.fillRelevancy}
                          </A>
                        </p>
                      )}
                    </Show>
                  </section>

                  <section class={styles.stack}>
                    <h3>{i18n.t().portal.requirements}</h3>
                    <Table
                      rows={requirementsOf(value, persona)}
                      rowKey={(requirement) => requirement.id}
                      emptyLabel={i18n.t().portal.noRequirements}
                      columns={[
                        {
                          key: "capability",
                          header: i18n.t().common.capability,
                          cell: (requirement) => capabilityLabel(value, requirement.capability),
                        },
                        {
                          key: "minimum",
                          header: i18n.t().reports.minimum,
                          cell: (requirement) => formatMinimum(i18n.t(), requirement.minimum, i18n.locale()),
                        },
                        {
                          key: "rationale",
                          header: i18n.t().personas.rationale,
                          cell: (requirement) => (
                            <span class={styles.rationale}>{requirement.rationale}</span>
                          ),
                        },
                      ]}
                    />
                  </section>
                </Panel>
              )}
            </For>
          </Show>
        )}
      </Async>
    </>
  );
};
